import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { apiService, SavedCircuit, Project } from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { CircuitSchema } from '../types';

interface CircuitBrowserProps {
  onClose: () => void;
  onLoadCircuit: (schema: CircuitSchema, title: string) => void;
  currentSchema: CircuitSchema;
}

export const CircuitBrowser: React.FC<CircuitBrowserProps> = ({ onClose, onLoadCircuit, currentSchema }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [circuits, setCircuits] = useState<SavedCircuit[]>([]);
  const [sharedCircuits, setSharedCircuits] = useState<SavedCircuit[]>([]);
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [tab, setTab] = useState<'my' | 'shared'>('my');
  const [loading, setLoading] = useState(true);

  // Save dialog state
  const [showSave, setShowSave] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveProjectId, setSaveProjectId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // New project dialog
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectsData, circuitsData, sharedData] = await Promise.all([
        apiService.getProjects(),
        apiService.getCircuits({ project: selectedProject || undefined }),
        apiService.getSharedWithMe(),
      ]);
      setProjects(projectsData);
      setCircuits(circuitsData);
      setSharedCircuits(sharedData);
    } catch {
      // silently fail
    }
    setLoading(false);
  }, [selectedProject]);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const handleSave = async () => {
    if (!saveTitle.trim()) return;
    setSaving(true);
    try {
      await apiService.saveCircuit({
        title: saveTitle,
        circuit_schema: currentSchema,
        project: saveProjectId || undefined,
      });
      setShowSave(false);
      setSaveTitle('');
      loadData();
    } catch {
      alert('Failed to save circuit');
    }
    setSaving(false);
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    try {
      const project = await apiService.createProject({ name: newProjectName });
      setShowNewProject(false);
      setNewProjectName('');
      setProjects(prev => [...prev, project]);
      setSaveProjectId(project.id);
    } catch {
      alert('Failed to create project');
    }
  };

  const handleLoadCircuit = async (circuit: SavedCircuit) => {
    try {
      const full = await apiService.getCircuit(circuit.id);
      onLoadCircuit(full.circuit_schema, full.title);
      onClose();
    } catch {
      alert('Failed to load circuit');
    }
  };

  const handleDelete = async (circuit: SavedCircuit) => {
    if (!window.confirm(`Delete "${circuit.title}"?`)) return;
    try {
      await apiService.deleteCircuit(circuit.id);
      loadData();
    } catch {
      alert('Failed to delete circuit');
    }
  };

  const handleDuplicate = async (circuit: SavedCircuit) => {
    try {
      await apiService.duplicateCircuit(circuit.id);
      loadData();
    } catch {
      alert('Failed to duplicate circuit');
    }
  };

  if (!user) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <Header>
          <Title>My Circuits</Title>
          <HeaderActions>
            <SaveBtn onClick={() => setShowSave(true)}>Save Current</SaveBtn>
            <CloseBtn onClick={onClose}>&times;</CloseBtn>
          </HeaderActions>
        </Header>

        <Tabs>
          <Tab $active={tab === 'my'} onClick={() => setTab('my')}>My Circuits</Tab>
          <Tab $active={tab === 'shared'} onClick={() => setTab('shared')}>Shared with Me</Tab>
        </Tabs>

        {tab === 'my' && (
          <>
            <ProjectBar>
              <ProjectSelect value={selectedProject || ''} onChange={e => setSelectedProject(e.target.value ? Number(e.target.value) : null)}>
                <option value="">All Projects</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.circuit_count})</option>
                ))}
              </ProjectSelect>
              <SmallBtn onClick={() => setShowNewProject(true)}>+ Project</SmallBtn>
            </ProjectBar>

            <CircuitList>
              {loading ? (
                <Empty>Loading...</Empty>
              ) : circuits.length === 0 ? (
                <Empty>No circuits saved yet. Save your current circuit to get started.</Empty>
              ) : (
                circuits.map(circuit => (
                  <CircuitCard key={circuit.id}>
                    <CircuitInfo onClick={() => handleLoadCircuit(circuit)}>
                      <CircuitTitle>{circuit.title || 'Untitled'}</CircuitTitle>
                      <CircuitMeta>
                        {circuit.description && <span>{circuit.description}</span>}
                        <span>Updated: {new Date(circuit.updated_at).toLocaleDateString()}</span>
                        {circuit.is_public && <PublicBadge>Public</PublicBadge>}
                      </CircuitMeta>
                    </CircuitInfo>
                    <CircuitActions>
                      <ActionBtn onClick={() => handleDuplicate(circuit)}>Duplicate</ActionBtn>
                      <ActionBtn $danger onClick={() => handleDelete(circuit)}>Delete</ActionBtn>
                    </CircuitActions>
                  </CircuitCard>
                ))
              )}
            </CircuitList>
          </>
        )}

        {tab === 'shared' && (
          <CircuitList>
            {sharedCircuits.length === 0 ? (
              <Empty>No circuits have been shared with you.</Empty>
            ) : (
              sharedCircuits.map(circuit => (
                <CircuitCard key={circuit.id}>
                  <CircuitInfo onClick={() => handleLoadCircuit(circuit)}>
                    <CircuitTitle>{circuit.title || 'Untitled'}</CircuitTitle>
                    <CircuitMeta>
                      <span>By: {circuit.owner_email}</span>
                      <span>Updated: {new Date(circuit.updated_at).toLocaleDateString()}</span>
                    </CircuitMeta>
                  </CircuitInfo>
                </CircuitCard>
              ))
            )}
          </CircuitList>
        )}

        {/* Save Dialog */}
        {showSave && (
          <InnerOverlay>
            <InnerModal>
              <InnerTitle>Save Circuit</InnerTitle>
              <Input placeholder="Circuit title" value={saveTitle} onChange={e => setSaveTitle(e.target.value)} autoFocus />
              <ProjectSelect value={saveProjectId || ''} onChange={e => setSaveProjectId(e.target.value ? Number(e.target.value) : null)} style={{ width: '100%' }}>
                <option value="">No project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </ProjectSelect>
              <BtnRow>
                <CancelBtn onClick={() => setShowSave(false)}>Cancel</CancelBtn>
                <SaveBtn onClick={handleSave} disabled={saving || !saveTitle.trim()}>{saving ? 'Saving...' : 'Save'}</SaveBtn>
              </BtnRow>
            </InnerModal>
          </InnerOverlay>
        )}

        {/* New Project Dialog */}
        {showNewProject && (
          <InnerOverlay>
            <InnerModal>
              <InnerTitle>New Project</InnerTitle>
              <Input placeholder="Project name" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} autoFocus />
              <BtnRow>
                <CancelBtn onClick={() => setShowNewProject(false)}>Cancel</CancelBtn>
                <SaveBtn onClick={handleCreateProject} disabled={!newProjectName.trim()}>Create</SaveBtn>
              </BtnRow>
            </InnerModal>
          </InnerOverlay>
        )}
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: white; border-radius: 12px;
  width: 640px; max-width: 90vw; max-height: 80vh;
  display: flex; flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.3);
  position: relative;
`;

const Header = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 20px 24px 12px;
`;

const HeaderActions = styled.div`
  display: flex; align-items: center; gap: 12px;
`;

const Title = styled.h2`
  margin: 0; font-size: 20px; color: #202D61;
`;

const CloseBtn = styled.button`
  background: none; border: none; font-size: 24px; color: #666; cursor: pointer;
  &:hover { color: #333; }
`;

const Tabs = styled.div`
  display: flex; border-bottom: 1px solid #eee; padding: 0 24px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 16px; background: none; border: none; font-size: 13px; cursor: pointer;
  color: ${p => p.$active ? '#202D61' : '#666'};
  border-bottom: 2px solid ${p => p.$active ? '#202D61' : 'transparent'};
  font-weight: ${p => p.$active ? '600' : '400'};
  &:hover { color: #202D61; }
`;

const ProjectBar = styled.div`
  display: flex; gap: 8px; padding: 12px 24px; align-items: center;
`;

const ProjectSelect = styled.select`
  padding: 6px 10px; border: 1px solid #ddd; border-radius: 4px; font-size: 13px;
`;

const SmallBtn = styled.button`
  padding: 6px 12px; border: 1px solid #ddd; background: white; border-radius: 4px;
  font-size: 12px; cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const CircuitList = styled.div`
  flex: 1; overflow-y: auto; padding: 8px 24px 24px;
`;

const CircuitCard = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 12px; border: 1px solid #eee; border-radius: 8px; margin-bottom: 8px;
  &:hover { border-color: #ccc; background: #fafafa; }
`;

const CircuitInfo = styled.div`
  flex: 1; cursor: pointer; min-width: 0;
`;

const CircuitTitle = styled.div`
  font-size: 14px; font-weight: 500; color: #333;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;

const CircuitMeta = styled.div`
  font-size: 12px; color: #888; display: flex; gap: 12px; margin-top: 4px;
`;

const PublicBadge = styled.span`
  background: #e3f2fd; color: #1565c0; padding: 1px 6px; border-radius: 4px; font-size: 11px;
`;

const CircuitActions = styled.div`
  display: flex; gap: 6px; flex-shrink: 0; margin-left: 12px;
`;

const ActionBtn = styled.button<{ $danger?: boolean }>`
  padding: 4px 10px; border: 1px solid ${p => p.$danger ? '#d93025' : '#ddd'};
  background: white; border-radius: 4px; font-size: 12px; cursor: pointer;
  color: ${p => p.$danger ? '#d93025' : '#333'};
  &:hover { background: ${p => p.$danger ? '#fce4ec' : '#f5f5f5'}; }
`;

const Empty = styled.div`
  text-align: center; padding: 40px 20px; color: #999; font-size: 14px;
`;

const InnerOverlay = styled.div`
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;
  border-radius: 12px;
`;

const InnerModal = styled.div`
  background: white; border-radius: 8px; padding: 24px; width: 360px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  display: flex; flex-direction: column; gap: 12px;
`;

const InnerTitle = styled.h3`
  margin: 0; font-size: 16px; color: #333;
`;

const Input = styled.input`
  padding: 8px 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 14px;
  outline: none;
  &:focus { border-color: #1a73e8; }
`;

const BtnRow = styled.div`
  display: flex; justify-content: flex-end; gap: 8px; margin-top: 4px;
`;

const CancelBtn = styled.button`
  padding: 8px 16px; background: white; border: 1px solid #ddd; border-radius: 6px;
  font-size: 13px; cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const SaveBtn = styled.button`
  padding: 8px 16px; background: #202D61; color: white; border: none; border-radius: 6px;
  font-size: 13px; cursor: pointer; font-weight: 500;
  &:hover { background: #2a3a7a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
